import React, { useState } from "react";
import { HiLocationMarker } from "react-icons/hi";
import { AppointmentsAPI } from "../../middleware";

type AppointmentCardProps = {
  image?: string;
  specialistName?: string;
  appointmentName?: string;
  appointmentCategoryTypeName?: string;
  appointmentDate?: Date;
  appointStartTimeName?: string;
  branchName?: string;
  status?: string;
  appointmentId: string; // Include appointmentId
  accessToken: string; // Include accessToken for API call
  healthFacilityId: string; // Include healthFacilityId
  patientId: string; // Include patientId
  onCancelClick: () => void; // Callback for cancel action
};

export const AppointmentCard: React.FC<AppointmentCardProps> = ({
  image,
  specialistName,
  appointmentName,
  appointmentCategoryTypeName,
  appointmentDate,
  appointStartTimeName,
  branchName,
  status,
  appointmentId,
  accessToken,
  healthFacilityId,
  patientId,
  onCancelClick // Use the onCancelClick prop
}) => {
  // State to handle the current status of the appointment
  const [isCanceled, setIsCanceled] = useState(false); // New state to track cancellation

  // Function to handle cancellation
  // const handleCancel = async () => {
  //   try {
  //     const response = await AppointmentsAPI.cancelAppointment(
  //       accessToken,
  //       healthFacilityId,
  //       patientId,
  //       appointmentId
  //     );
  //     if (response.status === 200 || response.status === 201) {
  //       setIsCanceled(true); // Set to true to hide the card
  //       onCancelClick(); // Call the onCancelClick callback
  //     } else {
  //       console.error("Failed to cancel appointment:", response.data.message);
  //     }
  //   } catch (error) {
  //     console.error("Error canceling appointment:", error);
  //   }
  // };

  // If the appointment is canceled, do not render the card
  if (isCanceled) {
    return null; // Card will not be rendered
  }

  const formatDate = (date: Date) => {
    const today = new Date();
    const tomorrow = new Date();
    tomorrow.setDate(today.getDate() + 1);

    if (date.toDateString() === tomorrow.toDateString()) {
      return 'Tomorrow';
    }

    const day = date.getDate();
    const month = date.toLocaleString('default', { month: 'long' });
    const year = date.getFullYear();

    const daySuffix = (d: number) => {
      if (d > 3 && d < 21) return 'th';
      switch (d % 10) {
        case 1: return 'st';
        case 2: return 'nd';
        case 3: return 'rd';
        default: return 'th';
      }
    };

    return `${day}${daySuffix(day)} ${month}, ${year}`;
  };

  return (
    <div
      className={`flex flex-col justify-center items-center rounded-3xl px-4 py-4 w-full md:w-[220px] xl:w-[268px] mt-4 xl:mt-0 hover:opacity-95 ${status === "canceled" ? "bg-red-400" : "bg-primary-500"}`}
    >
      {image && <img src={image} alt={appointmentName} className="w-[150px] md:w-[100px] rounded-full" />}
      <div className="flex flex-col visits-start">
        {specialistName && <p className="text-primary-100 text-lg font-bold mt-2 tracking-wide">{specialistName.length > 17 ? `${specialistName.substring(0, 17)}...` : specialistName}</p>}
        {appointmentName && <p className="text-primary-100 text-xs font-medium mt-1">{appointmentName}</p>}
        {appointmentCategoryTypeName && (
          <p className="text-primary-100 text-xs font-medium mt-1 underline">
            {appointmentCategoryTypeName === "null" ? "No category selected" : appointmentCategoryTypeName}
          </p>
        )}
        {appointmentDate && (
          <div className="text-primary-100 flex items-center space-x-2 mt-1">
            <p className="text-xs font-normal">
              {formatDate(new Date(appointmentDate))}
              {appointStartTimeName && ` at ${appointStartTimeName}`}
            </p>
          </div>
        )}
        <div className="flex flex-row pt-2">
          {branchName && (
            <div className="text-primary-100 flex items-center space-x-1 mt-1 w-full">
              <HiLocationMarker className="text-sm" />
              <p className="text-xs font-normal">{branchName ?? "No location selected"}</p>
            </div>
          )}
          <div className="flex flex-col gap-1 whitespace-nowrap">
            {appointStartTimeName && <button className="bg-blue-900 px-3 py-1 rounded-xl text-white">{appointStartTimeName}</button>}
            {status === "assigned" ? (
              <>
                <button className="bg-green-900 px-3 py-1 rounded-xl text-white">Pending</button>
                <button
                  className="bg-red-400 px-3 py-1 rounded-xl text-white mt-2 shadow-lg shadow-[#0000002c] hover:shadow-[#00000060] hover:shadow-lg duration-500"
                  onClick={onCancelClick}
                >
                  Cancel
                </button>
              </>
            ) : status === "Attended/Undischarged" ? (
              <button className="bg-green-900 px-3 py-1 rounded-xl text-white">Completed</button>
            ) : (
              <button className="bg-red-500 px-3 py-1 rounded-xl text-white">Cancelled</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};